



































































































































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";

// Enums/Data
import { CustomFieldsTypes } from "@/data/enums/customFields";
import { DataModules } from "@/store/modules/data";
import { ImageObjectTypes } from "@/models/images";
import { ImageUploadTypes } from "@/data/constants";

// Types
import { IBrand, ICustomField } from "@/models/brands";
import { IUploadFile } from "@/models/uploadFiles";
import { IState } from "@/store";
import { AxiosError } from "axios";

@Component({
  components: {
    "file-upload-field": () =>
      import("@/components/app/global/fields/fileUploadField.vue")
  },
  computed: {
    ...mapState({
      customFields(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: this.dataModule
        });
      }
    })
  }
})
export default class CustomFields extends Mixins(RequestPromises, Context) {
  // Getters
  public customFields!: ICustomField[];

  @Prop({ required: true })
  public value!: { custom_fields: any };

  @Prop({ default: () => [] })
  public externalCustomImages!: IUploadFile[];

  @Prop({ default: false })
  public isProcessing!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: true })
  public canManage!: boolean;

  @Prop({ default: true })
  public autocomplete!: boolean;

  @Prop({ required: true })
  public brandId!: IBrand["id"];

  @Prop({ required: true })
  public dataModule!:
    | DataModules.CUSTOM_FIELDS_CLIENTS
    | DataModules.CUSTOM_FIELDS_BASKET;

  @Prop({ default: null })
  public filter!: any;

  public imageObjectTypes = ImageObjectTypes;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;

  public customFieldsTypes = CustomFieldsTypes;
  public imageUploadTypes = ImageUploadTypes;

  public customImages: IUploadFile[] = [];

  public formValues = this.value;

  public created() {
    this.$watch("formValues", val => this.$emit("input", val), { deep: true });
    this.$watch("value", val => (this.formValues = val), { deep: true });

    this.loadData();
  }

  public async loadData() {
    try {
      await this.getCustomFields();
      await this.fillForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
      this.$emit("loaded");
    }
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  @Watch("customImages", { deep: true })
  public customImagesChanged() {
    this.$emit("update:externalCustomImages", this.customImages);
  }

  @Watch("brandId")
  public getCustomFields(ignoreStored = false) {
    return this.$store.dispatch(`data/${this.dataModule}/list`, {
      vm: this,
      params: {
        brand_id: this.brandId,
        limit: 0,
        order: "order"
      },
      ignoreStored
    });
  }

  public async fillForm() {
    const customImages: IUploadFile[] = [];
    this.$_.forEach(this.filteredCustomFields, async field => {
      if (
        field.type === CustomFieldsTypes.SELECT_RADIO &&
        this.formValues.custom_fields[field.code] === undefined
      ) {
        this.$set(this.formValues.custom_fields, field.code, false);
      }

      if (field.type === CustomFieldsTypes.IMAGE) {
        const customImage = this.getCustomImage(
          this.externalCustomImages,
          field
        );
        if (customImage !== undefined) {
          customImages.push(customImage);
          return;
        }
        customImages.push({
          id: field.id,
          code: field.code,
          file: null,
          isUploading: false,
          progress: 0,
          hash: this.formValues.custom_fields[field.code] || null
        });
      }
    });

    this.customImages = customImages;
  }

  public getCustomImage(images, field) {
    return this.$_.find(this.externalCustomImages, ["id", field.id]);
  }

  public get filteredCustomFields() {
    if (this.filter === null) {
      return this.customFields;
    }
    return this.$_.filter(this.customFields, this.filter);
  }

  public getFieldType(fieldType: number) {
    switch (fieldType) {
      case CustomFieldsTypes.TEXT:
        return "string";
      case CustomFieldsTypes.TEXTAREA:
        return "textarea";
      case CustomFieldsTypes.NUMBER:
        return "number";
      case CustomFieldsTypes.PASSWORD:
        return "password";
    }
  }

  public imageDeleted(fieldId) {
    this.customImages.splice(
      this.$_.findIndex(this.customImages, ["id", fieldId]),
      1
    );
  }

  public imageAdded(image, fieldId) {
    const index = this.$_.findIndex(this.customImages, ["id", fieldId]);

    this.customImages.splice(
      index !== -1 ? index : this.customImages.length,
      1,
      {
        id: fieldId,
        code: this.$_.get(
          this.$_.find(this.customFields, ["id", fieldId]),
          "code"
        ) as string,
        file: this.$_.cloneDeep(image),
        isUploading: false,
        progress: 0,
        hash: null
      }
    );
  }

  public async uploadCustomImages(isGuest = false) {
    const uploadImages = this.$_.filter(this.customImages, customImage => {
      return (
        customImage.hash !==
          this.$_.get(this.formValues, `custom_fields[${customImage.code}]`) &&
        this.$_.get(this.formValues, `custom_fields[${customImage.code}]`) !==
          undefined
      );
    });

    return Promise.all(
      this.$_.map(uploadImages, uploadImage => {
        return this.uploadImage(uploadImage, isGuest);
      })
    );
  }

  public async uploadImage(customImage, isGuest) {
    const data = new FormData();
    data.append("image", customImage?.file?.fileObj);
    try {
      const result = await this.$store.dispatch(
        `data/${DataModules.IMAGES}/uploadCustomFieldImage`,
        this.$_.merge(
          {
            fieldId: customImage.id,
            data,
            vm: this,
            onUploadProgress: progressEvent => {
              this.updateCustomImage(customImage.id, {
                progress: Math.floor(
                  (progressEvent.loaded * 100) / progressEvent.total
                ),
                isUploading: true
              });
            }
          },
          isGuest ? { guestToken: this.guestAccessToken } : {}
        )
      );

      this.formValues.custom_fields[customImage.code] = result.value;

      this.updateCustomImage(customImage.id, {
        progress: 0,
        isUploading: false,
        hash: result.value
      });

      return Promise.resolve();
    } catch (e) {
      const error = e as AxiosError;
      if (error?.response?.data?.error?.data?.image) {
        error.response.data.error.data[`custom_fields.${customImage.code}`] =
          error.response.data.error.data.image;
        delete error.response.data.error.data.image;
      }
      throw error;
    }
  }

  public updateCustomImage(fieldId: string, data) {
    const index = this.$_.findIndex(this.customImages, ["id", fieldId]);
    if (index > -1) {
      this.$set(
        this.customImages,
        index,
        this.$_.merge(this.customImages[index], data)
      );
    }
  }

  public isUploading(fieldId: string) {
    return this.$_.get(
      this.$_.find(this.customImages, ["id", fieldId]),
      "isUploading",
      false
    );
  }

  public updateImageUploadProgress(fieldId, percentCompleted) {
    this.customImages = this.$_.map(this.customImages, customImage => {
      if (customImage.id === fieldId) {
        customImage.progress = percentCompleted;
      }
      return customImage;
    });
  }

  public getProgress(fieldId) {
    return this.$_.get(
      this.$_.find(this.customImages, ["id", fieldId]),
      "progress",
      0
    );
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: this.dataModule,
      vm: this
    });
  }
}
